import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { filter, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AdminService } from 'src/app/admin/admin.service';
import { CloneApiActions, CloneUserActions } from 'src/app/admin/clone-user/store/clone.actions';
import * as Selectors from 'src/app/admin/clone-user/store/clone.selectors';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';
import { HttpResponseHelpers } from 'src/app/shared/helpers';

@Injectable()
export class CloneEffect {
    getEmployeeByName$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(CloneUserActions.getUsersByName),
            switchMap((action) => {
                return this._adminService.getUsersByName(action.name, action.companyId).pipe(
                    map((response) =>
                        CloneApiActions.getUsersByNameSuccess({
                            employee: response
                        })
                    ),
                    catchError((error: unknown) =>
                        of(CloneApiActions.getUsersByNameFailure({ error: HttpResponseHelpers.parseErrorResponse(error) }))
                    )
                );
            })
        );
    });

    setCloneCoreApiPermissions$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(CloneUserActions.setCloneCoreApiPermissions),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectCloneUserId),
                this.store.select(Selectors.selectHasCloneRestrictedPermissions)]),
            filter(([{ cloneCoreApiPermissions },, hasCloneRestrictedPermission]) => cloneCoreApiPermissions && hasCloneRestrictedPermission === null),
            switchMap(([, cloneUserId]) => {
                return this._adminService.userHasCloneRestrictedPermission(cloneUserId).pipe(
                    map((hasCloneRestrictedPermission) =>
                        CloneApiActions.userHasCloneRestrictedPermissionSuccess({
                            hasCloneRestrictedPermission
                        })
                    ),
                    catchError((error: unknown) =>
                        of(CloneApiActions.userHasCloneRestrictedPermissionFailure({ error: HttpResponseHelpers.parseErrorResponse(error) }))
                    )
                );
            })
        );
    });

    createCloneUser$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(CloneUserActions.createClone),
            switchMap((action) => {
                return this._adminService.createClone(action.userInfo).pipe(
                    map((response) =>
                        CloneApiActions.createCloneSuccess({
                            clonedUser: response
                        })
                    ),
                    catchError((error: unknown) =>
                        of(CloneApiActions.createCloneFailure({ error: HttpResponseHelpers.parseErrorResponse(error) }))
                    )
                );
            })
        );
    });

    displayToaster$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(CloneApiActions.createCloneFailure),
            map((action) =>
                showToasterMessage({
                    message: `Error: ${action.error}`,
                    duration: 3000
                })
            )
        );
    });

    clearClone$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(CloneUserActions.clearClone),
            map(() => CloneUserActions.clearCloneSuccess({ clonedUser: null }))
        );
    });

    goToClone$ = createEffect(
        () => {
            return this._actions$.pipe(
                ofType(CloneUserActions.clearCloneSuccess),
                map(() => {
                    this._router.navigate([`/admin/cloneuser`]);
                })
            );
        },
        { dispatch: false }
    );

    constructor(
        private readonly _actions$: Actions,
        private readonly store: Store,
        private readonly _adminService: AdminService,
        private readonly _router: Router
    ) {}
}
